/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import clsx from 'clsx'
import {Dispatch, FC, SetStateAction, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import Joyride, {TooltipRenderProps} from 'react-joyride'
import {useAuthUser} from 'react-auth-kit'
import {useIsMobile} from 'src/feature/bite/hook/useIsMobile'

function CustomTooltip(props: TooltipRenderProps) {
  const {backProps, closeProps, continuous, index, primaryProps, skipProps, step, tooltipProps} =
    props

  return (
    <div className='position-relative d-flex flex-column gap-5 align-items-end'>
      <div className='bg-white card shadow-sm rounded min-w-250px' {...tooltipProps}>
        {step.title && (
          <div className='card-header d-flex justify-content-between py-5'>
            <div className='card-title fs-3 fw-bolder text-dark'>{step.title}</div>
          </div>
        )}
        <div className='card-body fs-6 py-5'>{step.content}</div>
        <div className='card-footer d-flex justify-content-between py-5'>
          {index > 0 && (
            <button className='btn btn-sm bg-light me-2' {...backProps}>
              {backProps.title}
            </button>
          )}
          {continuous && (
            <button className='btn btn-sm bg-primary text-white' {...primaryProps}>
              {primaryProps.title}
            </button>
          )}
        </div>
      </div>

      <button className='btn btn-light d-flex align-items-center' {...skipProps}>
        <i className='bi bi-x-lg text-dark'></i>
        {skipProps.title}
      </button>
    </div>
  )
}

type Props = {
  link: string
  setLink: Dispatch<SetStateAction<string>>
}

const AsideTabs: FC<Props> = ({link, setLink}) => {
  const intl = useIntl()
  const auth = useAuthUser()

  const navigate = useNavigate()
  const location = useLocation() // Get the current location
  const scrollref = useRef<HTMLDivElement>(null)
  // const [height, setHeight] = useState(0)

  const handleTabClick = (selectedLink: string) => {
    setLink(selectedLink)
    navigate(`/${selectedLink.toLowerCase()}`)
  }

  const isMobile = useIsMobile()

  let tabs: Array<{
    link: string
    icon: string
    tooltip: string
    target?: string
    id: string
    disabled?: boolean
  }> = [
    /*     {
      link: 'dashboard',
      icon: 'bi-house',
      tooltip: intl.formatMessage({id: 'MENU.HOMEPAGE'}),
    }, */
    {
      link: 'members',
      id: 'members',
      icon: 'bi-person',
      tooltip: intl.formatMessage({id: 'MENU.MEMBER'}),
    },
    {
      link: 'Analytics',
      id: 'analytics',
      icon: 'bi-bar-chart-line',
      tooltip: intl.formatMessage({id: 'MENU.ANALYTICS'}),
    },
    {
      link: 'campaign',
      id: 'campaign',
      icon: 'bi-percent',
      disabled: process.env.REACT_APP_IS_CAMPAIGN_HIDDEN === 'true',
      tooltip: intl.formatMessage({id: 'MENU.CAMPAIGN'}),
    },
    {
      link: 'transaction-list',
      id: 'transaction-link',
      icon: 'bi-activity',
      tooltip: intl.formatMessage({id: 'MENU.TRANSACTIONS'}),
    },
    /*     {
      link: 'VIP',
      id: 'vip',
      icon: 'bi-gem',
      tooltip: intl.formatMessage({id: 'MENU.VIP'}),
      disabled: true,
    }, */
    /*     {
      link: 'Engagement',
      id: 'engagement',
      icon: 'bi-send',
      tooltip: intl.formatMessage({id: 'MENU.ENGAGEMENT'}),
    }, */
    {
      link: 'Packages',
      id: 'packages',
      icon: 'bi-box2',
      tooltip: intl.formatMessage({id: 'MENU.PACKAGES'}),
    },
    {
      link: 'Items',
      id: 'items',
      icon: 'bi-tag',
      tooltip: intl.formatMessage({id: 'MENU.PRODUCT'}),
    },
    /*    {
      link: 'https://docs.dejavooextra.com/',
      id: 'help',
      icon: 'bi-info-circle',
      tooltip: intl.formatMessage({id: 'MENU.HELP'}),
      target: '_blank',
    }, */
  ]

  const mobileTabs: Array<{
    link: string
    icon: string
    tooltip: string
    target?: string
    id: string
    disabled?: boolean
  }> = [
    {
      link: 'dashboard',
      id: 'dashboard',
      icon: 'bi-graph-up-arrow',
      tooltip: intl.formatMessage({id: 'MENU.DASHBOARD'}),
    },
    {
      link: 'members',
      id: 'members',
      icon: 'bi-person',
      tooltip: intl.formatMessage({id: 'MENU.MEMBER'}),
    },
    {
      link: 'transaction-list',
      id: 'transaction-link',
      icon: 'bi-activity',
      tooltip: intl.formatMessage({id: 'MENU.TRANSACTIONS'}),
    },
    {
      link: 'Packages',
      id: 'packages',
      icon: 'bi-box2',
      tooltip: intl.formatMessage({id: 'MENU.PACKAGES'}),
    },
    {
      link: 'Items',
      id: 'items',
      icon: 'bi-tag',
      tooltip: intl.formatMessage({id: 'MENU.PRODUCT'}),
    },
  ]

  if (auth()?.merchantBusinessType === 1) {
    tabs.push({
      link: 'Stores',
      id: 'stores',
      icon: 'bi-shop-window',
      tooltip: intl.formatMessage({id: 'MENU.STORE'}),
    })
  } else {
    tabs.push({
      link: 'Provider',
      id: 'provider',
      icon: 'bi-person',
      tooltip: intl.formatMessage({id: 'MENU.PROVIDER'}),
    })
  }

  const deviceControlledTabs = isMobile ? mobileTabs : tabs

  let activeLink = tabs.find((t) => location.pathname.includes(t.link.toLowerCase())) // Get current active tab
  if (!activeLink) {
    let param = location.search?.split('?')[1]?.split('=')[0]
    if (param && param === 'members') activeLink = tabs.find((t) => t.link === 'members')
    else if (param && param === 'stores') activeLink = tabs.find((t) => t.link === 'Stores')
  }
  const height = scrollref?.current?.getBoundingClientRect().height || 0

  return (
    <div
      className='hover-scroll-y pt-2 px-4'
      // {...(height !== 0 && {style: {height: `${height}px`, minHeight: '100%'}})}
      style={{
        // height: `${height}px`,
        ...{height: `${height}px`},
        minHeight: '100%',
      }}
      ref={scrollref}
    >
      <ul className='nav' id='kt_aside_nav_tabs' style={{display: 'block'}}>
        {deviceControlledTabs
          .filter((t) => !t.disabled)
          .map((t) => (
            <li key={t.link} className='nav-item py-1'>
              {t.target ? (
                <a
                  href={t.link}
                  id={t.id}
                  className={clsx(
                    'nav-link btn btn-icon btn-active-color-white btn-color-gray-400 btn-active-primary d-flex flex-column w-100 btn-active-style-none py-md-12 py-10 px-2'
                  )}
                  target={t.target}
                >
                  <span className={'bi ' + t.icon + ' fs-1'}></span>
                  <span className='fs-9 fs-lg-7 fw-bold pt-2'>{t.tooltip}</span>
                </a>
              ) : (
                <a
                  id={t.id}
                  href={`/${t.link.toLowerCase()}`}
                  className={clsx(
                    'nav-link btn btn-icon btn-active-color-white btn-color-gray-400 btn-active-primary d-flex flex-column w-100 btn-active-style-none py-md-12 py-10 px-2',
                    {active: t === activeLink} // Set active class based on activeLink
                  )}
                  onClick={() => {
                    handleTabClick(t.link)
                  }}
                >
                  <span className={'bi ' + t.icon + ' fs-1'}></span>
                  <span className='fs-9 fs-lg-7 fw-bold pt-2'>{t.tooltip}</span>
                </a>
              )}
            </li>
          ))}
      </ul>
    </div>
  )
}

export {AsideTabs}
